/** 用户协议正文（不含页面级滚动门槛逻辑，便于 /terms 与 /legal 复用） */
export default function TermsPageContent() {
  return (
    <>
      <h1 className="text-3xl font-bold mb-8">
        End User Service Agreement and Global Disclaimer
      </h1>

      <div className="space-y-6 text-gray-700">
        <p>Effective Date: March 24, 2026 &nbsp;|&nbsp; Last Updated: September 29, 2026</p>
        <p>
          Welcome to BamaBang (hereinafter referred to as "the Software" or "the Platform"). This End User Service Agreement and Global Disclaimer (hereinafter referred to as "this Agreement") is a legally binding formal contract between you and the BamaBang Operations Team (hereinafter referred to as "we", "us", or "the Operator").
        </p>
        <p>
          Before registering for, accessing, or using any services of the Software, please read this Agreement carefully. By checking "Agree" or actually starting to use the Platform's services, you signify that you have read, understood, and accepted all parts of this Agreement. If you do not agree, please stop the registration process immediately and uninstall the Software.
        </p>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 1: User Eligibility and Prerequisites
          </h2>
          <p>
            1.1 Absolute Age Limit: You must be at least 18 years old (or have reached the legal age of majority in your jurisdiction) to use the Software. The use of this Platform by minors is strictly prohibited. The Operator has the right to require official identification to verify your age in accordance with applicable laws. You shall bear all legal consequences arising from forged age or identity independently.
            <br />
            1.2 Criminal Background Declaration: By agreeing to this Agreement, you solemnly declare and guarantee that you have never been convicted of a felony, a sex offense, or any crime involving violence, and that you are not registered as a sex offender in any country or region.
            <br />
            1.3 Inactive Account Cleanup: To maintain platform efficiency and security, we reserve the right to periodically clean up and permanently delete accounts that have remained inactive for an extended period of time.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 2: Core Platform Disclaimers (Risk Assumption Principle)
          </h2>
          <p>
            2.1 Background Checks and Marital Status Disclaimer: BamaBang only provides technical services for online matching. We verify the age and identity of users through government-issued identification, but this verification confirms only that the document appears valid and that the user is an adult. We do not conduct criminal background checks, and we do not verify marital status, employment, income, assets or the true intentions of any user. The Platform assumes no direct or indirect legal or financial responsibility for the behavior, speech, or deceptive actions of any user on or off the Platform.
            <br />
            2.2 Offline Meetings and Personal Safety Disclaimer: You agree to assume all risks of interacting with other users. BamaBang and its Operator are not responsible for any personal injury, mental trauma, or property loss you may suffer.
            <br />
            2.3 Absolute Disclaimer for Financial Loss and Fraud: Under no circumstances should you send money, transfer funds, or disclose financial information to other users on this Platform. If you suffer property loss due to believing others and participating in any form of investment or lending, the Operator shall not bear any joint liability for compensation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 3: Data Access and Principle of Non-Intervention in Private Disputes
          </h2>
          <p>
            3.1 Non-Intervention in Private Litigation: The Platform shall absolutely not intervene in any private disputes, civil litigation, or arbitration arising between users.
            <br />
            3.2 Refusal of Private Evidence Requests: Unless a formal search warrant is received from a court or law enforcement agency with jurisdiction, the Platform strictly refuses to provide chat records or identity information to any single party.
            <br />
            3.3 User's Burden of Proof: Users have no right to require BamaBang to extract platform data as evidence for their personal legal proceedings.
            <br />
            3.4 Data Retention and Privacy: To protect user privacy, we do not permanently archive your chat records for monitoring or other secondary purposes. Text messages are automatically deleted from our servers 7 days after they are sent, and images and short videos are destroyed within 7 days when at least one person in the conversation has a paid membership or pass, or within 24 hours when both people use the free plan. Any remaining account data is permanently erased from our servers upon account deletion. Because of these short retention periods, we are generally unable to restore past conversations for any purpose.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 4: Global Usage and Specific Compliance Terms
          </h2>
          <p>
            Since the Software provides services in multiple countries and regions worldwide, users must comply with the local laws of their residence. All compliance risks for cross-border use are borne by the user.
            <br />
            4.1 English and French speaking regions: It is strictly forbidden to use the Platform for any form of prostitution, sexual transactions, or paid companionship arrangements.
            <br />
            4.2 Arabic speaking regions and Middle East: All consequences of the user's violation of local religious or moral regulations while using the Software shall be borne by the user.
            <br />
            4.3 Russian speaking regions and specific data jurisdictions: If your local laws conflict with the Platform's data processing methods, you should stop using it immediately.
            <br />
            4.4 Chinese speaking regions and other strictly reviewed regions: Any content posted by users represents their personal stance. The Platform has the right to directly delete and ban accounts for violating content.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 5: Community Rules and Prohibited Conduct
          </h2>
          <p>We maintain a zero-tolerance policy for objectionable content and abusive users. You are strictly prohibited from engaging in the following behaviors, otherwise we have the right to permanently ban your account:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Posting commercial advertisements, pyramid scheme information, or leading traffic to other platforms.</li>
            <li>Posting media content that contains full nudity, excessive exposure, or clear sexual suggestions.</li>
            <li>Continuously harassing or insulting other users, or posting hate speech or racial discrimination.</li>
            <li>Using robots to crawl platform data or engaging in reverse engineering.</li>
          </ul>
          <p className="mt-3">
            5.1 Reporting and Moderation: Every profile and conversation includes tools to report and block other users. We review reports of objectionable content and abusive behavior and act on them within 24 hours, which may include removing the content, suspending or permanently banning the account, and, where required, cooperating with law enforcement. Users who repeatedly violate these rules are ejected from the Platform.
            <br />
            5.2 Blocking: Blocking a user immediately removes them from your match recommendations and conversation list. Blocking is available without contacting us first.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 6: Content Authorization and Intellectual Property
          </h2>
          <p>
            Once you post content on the Platform, you grant the Operator a worldwide, royalty-free license to use and display such content within the legal scope.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 7: Virtual Assets and Subscriptions
          </h2>
          <p>
            In-app purchases within the Software do not have legal currency value. Within the app, digital entitlements are sold exclusively through Apple In-App Purchase on iOS and Google Play Billing on Android. Supporter codes distributed as crowdfunding rewards are governed separately by Chapter 8. VIP day passes are consumable goods: purchased days are added to your VIP balance and one day is deducted every calendar day while the balance is positive, whether or not you open the app. The trial pass tier may be purchased only once per account. VIP memberships may also be offered as auto-renewable subscriptions. If you subscribe, the subscription will automatically renew unless canceled at least 24 hours before the end of the current period. You can manage and cancel your subscriptions in your App Store or Google Play account settings. Except as required by applicable law or the app stores' refund policies, all virtual products and subscription services are non-refundable through our platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 8: Crowdfunding Supporter Codes
          </h2>
          <p>
            8.1 Supporter codes are issued as rewards to backers of our crowdfunding campaigns. Redeeming a code adds the stated number of VIP days to your account&apos;s VIP balance, with the same features as a VIP day pass. Redeemed days start on the day of redemption.
            <br />
            8.2 Each code has a redemption deadline stated at the time of issue, which is within six (6) months of the issue date. A code cannot be redeemed after its deadline, and unredeemed codes expire without refund.
            <br />
            8.3 A code can be redeemed once and only to an account that is a regular (non-guest) account and has passed age verification. Redeemed VIP days are tied to that account and cannot be transferred to another account, exchanged for cash or refunded. Days are deducted from your balance every calendar day, as described in Chapter 7.
            <br />
            8.4 Selling, auctioning or otherwise transferring codes for money is prohibited. We may invalidate codes obtained in violation of these Terms.
            <br />
            8.5 If your account is suspended or deleted for violating these Terms, any remaining VIP days are forfeited. If we discontinue the service, we will give at least thirty (30) days&apos; notice.
            <br />
            8.6 Codes are redeemed on our website. Codes cannot be redeemed inside the iOS app.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">
            Chapter 9: Dispute Resolution and Class Action Waiver (Critical)
          </h2>
          <p>
            9.1 Waiver of Class Action: Any dispute against the Platform can only be filed in your individual name. You explicitly waive the right to participate in any class action.
            <br />
            9.2 Applicable Law and Exclusive Jurisdiction: The interpretation of this Agreement and the resolution of disputes shall be governed by the laws of Japan. Both parties agree to submit to the Tokyo District Court of Japan as the exclusive court of first instance.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">Chapter 10: Contact Information</h2>
          <p>
            Operator: BamaBang Operations Team (Represented by プ ユエ)
            <br />
            Legal & Compliance Contact: Please use the in-app "Contact Us" feature.
            <br />
            Official Website: https://www.bamabang.net
          </p>
        </section>
      </div>
    </>
  );
}
